/**
 * Move in Month window (P1D-5529). The website requests the exact month with
 * `move_in_month_window=0`; a request (or property page URL) opts into the month on either side
 * with `move_in_month_window=1`. Pure, no imports: used by Search, PropertyPage and the server.
 */
export const MOVE_IN_MONTH_KEY = "move_in_month";
export const MOVE_IN_MONTH_WINDOW_KEY = "move_in_month_window";
export const WIDENED_MONTH_WINDOW = "1";
/**
 * The website asks for the exact month itself (decision 2026-10-21): backends keep their ±1
 * default for other callers, so every search / property-page API request with a month sends "0".
 */
export const STRICT_MONTH_WINDOW = "0";

const hasMonth = (value: unknown) =>
  value !== undefined && value !== null && String(value).trim() !== "";

const isWidenedValue = (value: unknown) =>
  value !== undefined && value !== null && String(value).trim() === WIDENED_MONTH_WINDOW;

/** Copy of `params` with the widened window set when a month is present, removed otherwise. */
export const withWidenedMonth = <T extends Record<string, any>>(params: T): T => {
  const next: Record<string, any> = { ...params };
  if (hasMonth(next[MOVE_IN_MONTH_KEY])) next[MOVE_IN_MONTH_WINDOW_KEY] = WIDENED_MONTH_WINDOW;
  else delete next[MOVE_IN_MONTH_WINDOW_KEY];
  return next as T;
};

/**
 * API params only — never the browser URL, `filter_keys`, canonicals or cache keys. Copy of
 * `params` with the strict window ("0") set when a month is present and no window is set yet
 * (an existing window, e.g. a widened "1", is kept); the window is removed when there is no month.
 */
export const withStrictMonth = <T extends Record<string, any>>(params: T): T => {
  const next: Record<string, any> = { ...params };
  if (!hasMonth(next[MOVE_IN_MONTH_KEY])) delete next[MOVE_IN_MONTH_WINDOW_KEY];
  else if (!hasMonth(next[MOVE_IN_MONTH_WINDOW_KEY])) {
    next[MOVE_IN_MONTH_WINDOW_KEY] = STRICT_MONTH_WINDOW;
  }
  return next as T;
};

/**
 * For `inventory_filter_count` requests only: like `withStrictMonth`, but also sets the strict
 * window when no month is selected, so the per-month option counts match the strict results the
 * student gets once a month is picked (the backend applies the window to the month buckets even
 * without a month, and ignores it everywhere else). An existing window is kept.
 */
export const withStrictMonthCounts = <T extends Record<string, any>>(params: T): T => {
  const next: Record<string, any> = { ...params };
  if (!hasMonth(next[MOVE_IN_MONTH_WINDOW_KEY]))
    next[MOVE_IN_MONTH_WINDOW_KEY] = STRICT_MONTH_WINDOW;
  return next as T;
};

/** True when `params` carries a non-empty month AND window "1" (trimmed). */
export const hasWidenedMonth = (params: Record<string, any> = {}): boolean =>
  hasMonth(params?.[MOVE_IN_MONTH_KEY]) && isWidenedValue(params?.[MOVE_IN_MONTH_WINDOW_KEY]);

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/** "nov-2026" shifted by `delta` months ("oct-2026" for -1); null when not a `mon-yyyy` month. */
const shiftMonth = (month: string, delta: number): string | null => {
  const [mon, year] = String(month).trim().toLowerCase().split("-");
  const index = MONTHS.indexOf(mon);
  if (index === -1 || !/^\d{4}$/.test(year || "")) return null;
  const total = Number(year) * 12 + index + delta;
  return `${MONTHS[((total % 12) + 12) % 12]}-${Math.floor(total / 12)}`;
};

/**
 * The property page URL value for a widened month: the selected month first, then the months
 * before and after ("nov-2026,oct-2026,dec-2026"). Students never see `move_in_month_window`;
 * the property page turns this back into the month plus window "1" for the API. Null when
 * `month` is not a `mon-yyyy` month.
 */
export const widenedMonthParam = (month: string): string | null => {
  const before = shiftMonth(month, -1);
  const after = shiftMonth(month, 1);
  if (!before || !after) return null;
  return [String(month).trim().toLowerCase(), before, after].join(",");
};

/**
 * Property page URL reader for `move_in_month`: the selected month (the first value), and whether
 * the value is exactly the widened list for it. Any other list counts as that single month.
 */
export const parseMoveInMonthParam = (raw?: string | null): { month: string; widened: boolean } => {
  const values = String(raw || "")
    .split(",")
    .map((v) => v.trim().toLowerCase())
    .filter(Boolean);
  const month = values[0] || "";
  return { month, widened: values.length === 3 && values.join(",") === widenedMonthParam(month) };
};
